import {
  AutoComplete,
  FormContainer,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { HTMLAttributes, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IProfileReport } from "../../../../../../models/profile-report";
import {
  createRoleReports,
  listReports,
  updateRoleReports,
} from "../../../../../../services/profileReports.service";
import { profileReportSchema, TProfileReportForm } from "./profileReportSchema";

const translationPath = "page.register.roles.";

interface IProfileReportProps {
  id?: string;
  roleId?: string;
  rows: IProfileReport[];
  onBack: () => void;
}

export const ProfileReport = memo(
  ({ id, roleId, rows, onBack }: IProfileReportProps) => {
    const { t } = useTranslation();

    const { sendRequest: create, loading: cLoading } = useFetch(
      createRoleReports,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateRoleReports,
    );

    const row = useMemo(
      () => rows.find((currentRow) => currentRow.id === id) ?? null,
      [rows, id],
    );

    const getReportOptionLabel = useCallback((option: ISelectType) => {
      const name = option?.name ?? "";
      const code = option?.code;

      if (code === undefined || code === null || code === "") return name;

      return `#${code} - ${name}`;
    }, []);

    const renderReportOption = useCallback(
      (props: HTMLAttributes<HTMLLIElement>, option: ISelectType) => (
        <li {...props} key={option.id}>
          {getReportOptionLabel(option)}
        </li>
      ),
      [getReportOptionLabel],
    );

    const getHandler = useCallback(
      async (): Promise<Treturn<TProfileReportForm>> => {
        const formData: TProfileReportForm = {
          report: row
            ? {
                id: row.report.id,
                name: row.report.name,
                code: row.report.code,
              }
            : null,
        };

        return {
          data: formData,
          success: true,
        };
      },
      [row],
    );

    const updateReportIds = useCallback(
      (reportId: string) => {
        return Array.from(
          new Set(
            rows.map((currentRow) =>
              currentRow.id === row?.id ? reportId : currentRow.reportId
            )
          )
        );
      },
      [row?.id, rows],
    );

    const saveHandler = (v: TProfileReportForm) =>
      create({
        roleId: roleId ?? "",
        reportIds: v.report ? [v.report.id] : [],
      });

    const updateHandler = (v: TProfileReportForm, id: string) =>
      update({
        id: roleId ?? "",
        item: {
          roleId: roleId ?? "",
          reportIds: v.report
            ? updateReportIds(v.report.id)
            : rows.map((currentRow) => currentRow.reportId),
        },
      });

    const loading = cLoading || uLoading;

    return (
      <FormContainer<TProfileReportForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "report")}
        schema={profileReportSchema}
        isModalForm={true}
        subtitle={
          id
            ? t(translationPath + "edit_link")
            : t(translationPath + "add_report")
        }
        backButtonOnClick={onBack}
        redirectOnSave={false}
        onSuccess={onBack}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <AutoComplete
                request={listReports}
                name="report"
                size="small"
                getOptionLabel={getReportOptionLabel}
                renderOption={renderReportOption}
                label={t(translationPath + "report")}
              />
            </Grid>
          </Grid>
        )}
      />
    );
  },
);
